import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { END_POINT, METHODS } from "../../../helper/helper";
import { fetchData } from "../../../helper/ApiCall";
import { useDispatch } from "react-redux";
import { userdataUpdate } from "../../../redux/reducers/userReducer";
import Swal from "sweetalert2";
import OTPVerification from "../../../components/OTPVerification";

const ClientLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setpassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");
  const [showOTP, setShowOTP] = useState(false);
  const [otpData, setOtpData] = useState({ userId: "", userType: "", userEmail: "" });

  const LoginHandler = async () => {
    if (!email || !password) {
      Swal.fire({
        title: "Missing Information",
        text: "Please enter both email and password",
        icon: "warning",
        confirmButtonColor: "#4F46E5",
        confirmButtonText: "OK",
      });
      return;
    }
    setLoading(true);
    try {
      const result = await fetchData(
        END_POINT.ClientLogin,
        METHODS.POST,
        JSON.stringify({ email, password })
      );
      if (result.success === true) {
        dispatch(userdataUpdate(result.data));
        navigate("/user/user-dashboard");
      } else if (result.requiresOTP) {
        setOtpData({ userId: result.userId, userType: result.userType, userEmail: email });
        setShowOTP(true);
      } else {
        Swal.fire({
          title: "Login Failed",
          text: result.message,
          icon: "error",
          confirmButtonColor: "#4F46E5",
          confirmButtonText: "Try Again",
        });
      }
    } catch {
      Swal.fire({
        title: "Error",
        text: "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#4F46E5",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSuccess = (userData) => {
    dispatch(userdataUpdate(userData));
    navigate("/user/user-dashboard");
    setShowOTP(false);
  };

  const handleOTPCancel = () => {
    setShowOTP(false);
    setOtpData({ userId: "", userType: "", userEmail: "" });
  };

  const onKey = (e) => { if (e.key === "Enter") LoginHandler(); };

  if (showOTP) {
    return (
      <OTPVerification
        userId={otpData.userId}
        userType={otpData.userType}
        userEmail={otpData.userEmail}
        onVerificationSuccess={handleOTPSuccess}
        onCancel={handleOTPCancel}
      />
    );
  }

  const inp = (field) => ({
    width: "100%",
    padding: "12px 16px 12px 42px",
    fontSize: "14px",
    border: "1.5px solid " + (focused === field ? "#4F46E5" : "#E8ECF4"),
    borderRadius: "10px",
    outline: "none",
    background: focused === field ? "#FAFBFF" : "#F7F8FC",
    color: "#1A202C",
    fontFamily: "inherit",
    transition: "all 0.2s",
    boxShadow: focused === field ? "0 0 0 3px rgba(79,70,229,0.1)" : "none",
    boxSizing: "border-box",
  });

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Inter', system-ui, sans-serif;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }
    input::placeholder { color: #A0AEC0; }
    @keyframes fadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.5; } }
  `;

  return (
    <>
      <style>{css}</style>

      {/* Background orbs */}
      <div style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: "-10%", left: "-10%", width: "50vw", height: "50vw", borderRadius: "50%", background: "rgba(255,255,255,0.06)", filter: "blur(60px)" }} />
        <div style={{ position: "absolute", bottom: "-10%", right: "-5%", width: "40vw", height: "40vw", borderRadius: "50%", background: "rgba(255,255,255,0.05)", filter: "blur(50px)" }} />
        <div style={{ position: "absolute", top: "40%", left: "60%", width: "20vw", height: "20vw", borderRadius: "50%", background: "rgba(255,255,255,0.04)", filter: "blur(40px)" }} />
      </div>

      {/* Card */}
      <div style={{
        position: "relative", zIndex: 1,
        background: "#FFFFFF", borderRadius: "24px",
        padding: "40px 36px 36px",
        width: "100%", maxWidth: "520px",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        animation: "fadeUp 0.4s ease both",
      }}>

        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "10px",
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            borderRadius: "16px", padding: "10px 20px", marginBottom: "20px",
            boxShadow: "0 4px 16px rgba(102,126,234,0.35)",
          }}>
            <span style={{ fontSize: "22px" }}>🤖</span>
            <span style={{ fontSize: "22px", fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" }}>AI Hub</span>
          </div>
          <h1 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "6px" }}>Welcome back</h1>
          <p style={{ fontSize: "13px", color: "#94a3b8" }}>Sign in to your client account</p>
        </div>

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "20px" }}>

          {/* Email */}
          <div>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748b", marginBottom: "6px", letterSpacing: "0.06em", textTransform: "uppercase" }}>Email Address</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", pointerEvents: "none" }}>✉️</span>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused("")}
                onKeyDown={onKey}
                style={inp("email")}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <label style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase" }}>Password</label>
             
            </div>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", pointerEvents: "none" }}>🔒</span>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setpassword(e.target.value)}
                onFocus={() => setFocused("password")}
                onBlur={() => setFocused("")}
                onKeyDown={onKey}
                style={{ ...inp("password"), paddingRight: "44px" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: "2px", color: "#94a3b8", transition: "color 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.color = "#4F46E5"}
                onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}
              >{showPassword ? "🙈" : "👁️"}</button>
            </div>
          </div>
        </div>

        {/* Sign In Button */}
        <button
          onClick={LoginHandler}
          disabled={loading}
          style={{
            width: "100%", padding: "13px",
            background: loading
              ? "#C7D2FE"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff", border: "none", borderRadius: "12px",
            fontSize: "15px", fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "inherit", transition: "all 0.2s",
            boxShadow: loading ? "none" : "0 4px 16px rgba(102,126,234,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            marginBottom: "10px",
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.boxShadow = "0 8px 24px rgba(102,126,234,0.5)"; }}
          onMouseLeave={(e) => { if (!loading) e.currentTarget.style.boxShadow = "0 4px 16px rgba(102,126,234,0.4)"; }}
        >
          {loading ? (
            <>
              <div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              Signing in...
            </>
          ) : "Sign In →"}
        </button>

        {/* Register Button */}
        <button
          onClick={() => navigate("/signup")}
          style={{
            width: "100%", padding: "12px",
            background: "transparent",
            border: "1.5px solid #C7D2FE",
            color: "#4F46E5", borderRadius: "12px",
            fontSize: "14px", fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit",
            transition: "all 0.15s", marginBottom: "24px",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#EEF2FF"; e.currentTarget.style.borderColor = "#A5B4FC"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#C7D2FE"; }}
        >Create an Account</button>

        

       
      </div>
    </>
  );
};

export default ClientLogin;